import { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Camera,
  Upload,
  Sparkles,
  X,
  Loader2,
  ImageIcon,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

export default function ScanRapportinoPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [files, setFiles] = useState([]); // {file, url}
  const [scanning, setScanning] = useState(false);

  const addFiles = (newFiles) => {
    const arr = Array.from(newFiles || []);
    if (!arr.length) return;
    if (files.length + arr.length > 7) {
      toast.error("Massimo 7 foto per scansione");
      return;
    }
    const next = arr.map((f) => ({
      _uid: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
      file: f,
      url: URL.createObjectURL(f),
    }));
    setFiles([...files, ...next]);
  };

  const removeFile = (idx) => {
    const f = files[idx];
    URL.revokeObjectURL(f.url);
    setFiles(files.filter((_, i) => i !== idx));
  };

  const handleScan = async () => {
    if (files.length === 0) {
      toast.error("Aggiungi almeno una foto");
      return;
    }
    setScanning(true);
    try {
      const fd = new FormData();
      files.forEach(({ file }) => fd.append("files", file));
      const { data } = await api.post("/rapportini/scan", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      const matchedEmp = data.matches?.employees?.filter((m) => m.matched).length || 0;
      const totalEmp = data.matches?.employees?.length || 0;
      const matchedComm = data.matches?.commesse?.filter((m) => m.matched).length || 0;
      const totalComm = data.matches?.commesse?.length || 0;
      toast.success(
        `Scansione completata: ${data.giorni?.length || 0} giorni · ${matchedEmp}/${totalEmp} dipendenti riconosciuti · ${matchedComm}/${totalComm} commesse`
      );
      // Pass parsed data to the form via location state
      navigate("/rapportino/new", { state: { ocr: data } });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Scansione non riuscita";
      toast.error(typeof msg === "string" ? msg : "Scansione non riuscita");
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-6 space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/">
          <Button variant="outline" size="icon" className="h-11 w-11">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
            Scansione AI
          </div>
          <h1 className="font-heading font-black text-2xl md:text-3xl tracking-tight">
            Scansiona rapportino cartaceo
          </h1>
        </div>
      </div>

      <Card className="p-5 space-y-3 bg-gradient-to-br from-[#F0F4FF] to-white border-[#0044FF]/20">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md bg-[#0044FF] text-white grid place-items-center shrink-0">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <div className="font-heading font-bold text-base">
              Riconoscimento intelligente con AI
            </div>
            <p className="text-sm text-slate-600 mt-1">
              Scatta una foto del rapportino cartaceo (anche scritto a mano).
              L'AI legge nomi, ore, commesse, annotazioni e pre-compila il
              modulo. Tu rivedi e correggi prima di salvare.
            </p>
            <div className="text-xs text-slate-500 mt-2">
              Carica fino a <b>7 foto</b> (es. 5 foto = 5 giorni di settimana).
              JPG, PNG, WEBP, max 10 MB l'una.
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-5 space-y-4">
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={() => cameraInputRef.current?.click()}
            disabled={scanning || files.length >= 7}
            className="h-12 bg-[#0044FF] hover:bg-[#0033CC]"
            data-testid="scan-camera-button"
          >
            <Camera className="h-5 w-5 mr-2" /> Scatta foto
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={scanning || files.length >= 7}
            className="h-12"
            data-testid="scan-upload-button"
          >
            <Upload className="h-5 w-5 mr-2" /> Da galleria
          </Button>
          <span className="text-xs text-slate-500 self-center ml-auto">
            {files.length}/7
          </span>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => addFiles(e.target.files)}
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            className="hidden"
            onChange={(e) => addFiles(e.target.files)}
          />
        </div>

        {files.length === 0 ? (
          <div
            className="border-2 border-dashed border-slate-300 rounded-md py-10 text-center text-slate-500"
            data-testid="scan-empty-state"
          >
            <ImageIcon className="h-10 w-10 mx-auto mb-2 text-slate-300" />
            <div className="text-sm font-bold">Nessuna foto caricata</div>
            <div className="text-xs mt-1">
              Inquadra il rapportino con luce naturale, in piano, senza ombre.
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {files.map((f, idx) => (
              <div
                key={f._uid}
                className="relative aspect-square rounded-md overflow-hidden border border-slate-200 bg-slate-100"
                data-testid={`scan-thumb-${idx}`}
              >
                <img
                  src={f.url}
                  alt={`Foto ${idx + 1}`}
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => removeFile(idx)}
                  disabled={scanning}
                  className="absolute top-1 right-1 h-7 w-7 rounded-full bg-black/70 text-white grid place-items-center hover:bg-red-600"
                  data-testid={`scan-remove-${idx}`}
                >
                  <X className="h-4 w-4" />
                </button>
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1.5 py-0.5 text-center">
                  Foto {idx + 1}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-4 bg-amber-50 border-amber-200">
        <div className="flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-900">
            <b>Consigli per una scansione precisa:</b> 1) inquadra l'intero
            foglio, 2) buona illuminazione naturale, 3) niente ombre/riflessi,
            4) testo leggibile. La grafia chiara aumenta moltissimo la
            precisione.
          </div>
        </div>
      </Card>

      <div className="flex justify-end gap-3">
        <Link to="/">
          <Button variant="outline" className="h-12">Annulla</Button>
        </Link>
        <Button
          onClick={handleScan}
          disabled={scanning || files.length === 0}
          data-testid="scan-submit-button"
          className="h-12 bg-[#0044FF] hover:bg-[#0033CC] min-w-[200px]"
        >
          {scanning ? (
            <>
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              Riconoscimento in corso…
            </>
          ) : (
            <>
              <Sparkles className="h-5 w-5 mr-2" />
              Scansiona con AI
            </>
          )}
        </Button>
      </div>

      {scanning && (
        <Card className="p-5 bg-slate-50">
          <div className="flex items-center gap-3 text-sm text-slate-700">
            <Loader2 className="h-5 w-5 animate-spin text-[#0044FF]" />
            <div>
              <div className="font-bold">L'AI sta leggendo il rapportino…</div>
              <div className="text-xs text-slate-500 mt-0.5">
                Solitamente 5-15 secondi a seconda del numero di foto.
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
